/** Suffixes by power of a thousand; index 0 is the un-suffixed range. */
const COMPACT_UNITS = ["", "k", "M", "B"] as const;

/**
 * Shortens a count so it fits a narrow column: 999 stays 999, 1000 becomes 1k,
 * 12345 becomes 12.3k, 1234567 becomes 1.2M.
 *
 * Leaderboard points are rendered in a one-line cell inside the right-hand
 * panel, where a fully written-out figure either wraps or gets ellipsised —
 * "1,234,567" is worse than useless when it renders as "1,234…".
 *
 * Rounding is applied before the unit is chosen, so a value just under a
 * boundary is promoted rather than reported as "1000k".
 */
export const formatCompactNumber = (value?: number | null): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "0";
  }

  const sign = value < 0 ? "-" : "";
  let scaled = Math.abs(value);
  let unit = 0;

  while (scaled >= 1000 && unit < COMPACT_UNITS.length - 1) {
    scaled /= 1000;
    unit += 1;
  }

  let rounded = Math.round(scaled * 10) / 10;

  // 999_999 scales to 999.999k, which rounds to 1000k — carry it to the next
  // unit instead so the suffix always leads the mantissa.
  if (rounded >= 1000 && unit < COMPACT_UNITS.length - 1) {
    rounded = Math.round((rounded / 1000) * 10) / 10;
    unit += 1;
  }

  // Below a thousand there is no unit and no reason for a decimal.
  if (unit === 0) return `${sign}${Math.round(rounded)}`;

  const mantissa =
    rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1);
  return `${sign}${mantissa}${COMPACT_UNITS[unit]}`;
};
